/**
 * Mecánica real de cada juego — DATO, no código.
 *
 * ⚠️ N1 · NADA DE ACÁ ES INVENTADO. Una mecánica inventada es tan grave como
 * un pozo inventado: el visitante se lleva a la agencia una idea equivocada
 * de cómo se juega, y se la dio el sitio del organismo que regula el juego.
 *
 * Regla de admisión: **un juego entra a este archivo solo si su mecánica se
 * pudo verificar contra el sitio oficial del juego o contra un artefacto del
 * propio repositorio.** Cada entrada declara su fuente en `fuente` y el
 * detalle largo está en la tabla de abajo. El juego que no se pudo verificar
 * queda afuera — está la lista al pie con el motivo de cada exclusión.
 *
 * Lo que este archivo NO declara, a propósito:
 *  · **Montos y premios.** Ninguno. No hay una sola cifra de dinero.
 *  · **Días y frecuencia de sorteo.** Hay tres fuentes internas que se
 *    contradicen (ver "Contradicciones detectadas"), así que no se publica
 *    ninguna. El simulador enseña la forma de la jugada, no el calendario.
 *
 * ── Fuentes, verificadas el 20/08/2026 ────────────────────────────────────
 *
 *  tombola (quiniela)
 *      "Se puede apostar a números de 1 a 4 cifras, entre el 0000 y el 9999"
 *      y "Seleccioná la posición de salida (del 1 al 20)".
 *      → quiniela.loteriadelaciudad.gob.ar (Lotería de la Ciudad, oficial).
 *      Que la posición llegue hasta 20 se corrobora contra el dato real de
 *      Jujuy: `extractos/{fecha}.quiniela[].numeros` guarda exactamente
 *      **20 números de 4 dígitos** por turno y por lotería
 *      (docs/arquitectura/extractos-loteria.md), y el 1º es la cabeza
 *      (`useExtractos.js`, marquesina "turnos de Jujuy con su cabeza").
 *      NO se publican multiplicadores de premio: los que publica Lotería de
 *      la Ciudad son suyos y no se verificó que rijan para la Tómbola Jujeña.
 *
 *  lotoplus
 *      "Elegí 6 números del 0 al 45", "Elegí un número del 0 al 9 y triplicá
 *      tu premio", "Con un solo ticket, participás en las 4 modalidades".
 *      → loto.loteriadelaciudad.gob.ar (LOTBA, oficial).
 *      Corroborado en el repo dos veces: el XML oficial firmado
 *      `functions/sorteos/__fixtures__/LTO51X20260815.xml` trae las cuatro
 *      modalidades con 6 <N0x> cada una, y `functions/sorteos/parser.js`
 *      declara `rangoNumeros: [0, 45]` y `RANGOS_POR_MODALIDAD.plus = [0, 9]`.
 *
 *  quini6
 *      "elegir 6 números sobre un total de 46 que van desde el '00' al '45
 *      inclusive'" y las modalidades TRADICIONAL (Primer Sorteo y Segunda del
 *      Quini), REVANCHA y SIEMPRE SALE, con la aclaración de que "para
 *      participar en Revancha y Siempre Sale, primero debe apostarse a la
 *      modalidad Tradicional".
 *      → loteriasantafe.gov.ar/index.php/contacto/preguntas-frecuentes-quini6
 *
 *  brinco
 *      "se extraen 6 números de un bolillero que contiene un total de 40
 *      bolillas con números que van desde el 0 hasta el 39", con premios por
 *      6, 5, 4 y 3 aciertos y la variante Brinco Junior Siempre Sale.
 *      → loteriasantafe.gov.ar/index.php/contacto/preguntas-frecuentes-brinco
 *
 *  loto5plus
 *      "Elegí 5 números entre el 0 y el 36" y "El sorteo se realiza con una
 *      ruleta francesa: el croupier tira las suertes hasta obtener 5 números
 *      distintos".
 *      → loto5.loteriadelaciudad.gob.ar (LOTBA, oficial).
 *
 * ── Contradicciones detectadas, NO corregidas (A5) ────────────────────────
 *  1. `functions/sorteos/parser.js` declara `loto5plus.rangoNumeros: [0, 45]`.
 *     El sitio oficial dice **0 a 36**. El validador del backend deja pasar
 *     un 40 en Loto 5 Plus, que no existe en ese bolillero. Acá manda el
 *     sitio oficial.
 *  2. `HomeComercial.jsx` describe Brinco como "Cinco números". El
 *     reglamento oficial dice **seis**. Acá manda el reglamento.
 *  3. Días de sorteo: la ficha de `HomeComercial.jsx` dice "Viernes" para
 *     Loto 5 Plus, `parser.js` dice sábado y lo marca como *supuesto*, y el
 *     XML oficial sortea sábado 15/08 con próximo sábado 22/08. Por eso este
 *     archivo no publica ningún día.
 *
 * ── Juegos que quedaron AFUERA, con el motivo ─────────────────────────────
 *  · **Telekino** — telekino.com.ar no publica la mecánica: la home solo trae
 *    resultados y `/como-se-juega` devuelve 404. Sin fuente, no entra.
 *  · **Pozo de la Quiniela** — no se encontró reglamento publicado de la
 *    Super Bola ni en loterialajujeña.com.ar ni en loteriasantafe.gov.ar.
 *  · **Lotería (billete)** — no hay nada que elegir: el billete viene
 *    numerado. No es un juego de selección y un selector le mentiría al
 *    visitante sobre cómo se juega.
 */

/**
 * Tabla de los sueños de la quiniela, del 00 al 99. El índice ES el número.
 *
 * Vivía dentro de `NumeroSuerte.jsx` como pie de página de un número
 * aleatorio; se mudó acá para que sea una pieza propia y buscable.
 *
 * Qué es y qué no es: es **tradición popular**, no una regla del juego. No
 * cambia la mecánica ni la probabilidad de nada, y así se dice en pantalla.
 */
export const TABLA_SUENOS = [
  'los huevos', 'el agua', 'el niño', 'San Cono', 'la cama', 'el gato', 'el perro', 'el revólver', 'el incendio', 'el arroyo',
  'la leche', 'el minero', 'el soldado', 'la yeta', 'el borracho', 'la niña bonita', 'el anillo', 'la desgracia', 'la sangre', 'el pescado',
  'la fiesta', 'la mujer', 'el loco', 'el cocinero', 'el caballo', 'la gallina', 'la misa', 'el peine', 'el cerro', 'San Pedro',
  'Santa Rosa', 'la luz', 'el dinero', 'la edad de Cristo', 'la cabeza', 'el pajarito', 'la manteca', 'el dentista', 'las piedras', 'la lluvia',
  'el cura', 'el cuchillo', 'las zapatillas', 'el balcón', 'la cárcel', 'el vino', 'los tomates', 'el muerto', 'el muerto que habla', 'la carne',
  'el pan', 'el serrucho', 'madre e hijo', 'el barco', 'la vaca', 'la música', 'la caída', 'el jorobado', 'el ahogado', 'las plantas',
  'la virgen', 'la escopeta', 'la inundación', 'el casamiento', 'el llanto', 'el cazador', 'la lombriz', 'la mordida', 'los sobrinos', 'los vicios',
  'el sueño', 'el excremento', 'la sorpresa', 'el hospital', 'la escalera', 'los besos', 'las llamas', 'las piernas', 'la ramera', 'el ladrón',
  'la bocha', 'las flores', 'la pelea', 'el mal tiempo', 'la iglesia', 'la linterna', 'el humo', 'los piojos', 'el Papa', 'la rata',
  'el miedo', 'el excusado', 'el médico', 'el enamorado', 'el cementerio', 'los anteojos', 'el marido', 'la mesa', 'el lavadero', 'el hermano',
];

/** Saca acentos y baja a minúsculas, para comparar sin que la tilde estorbe. */
export function normalizar(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Busca en la tabla de los sueños por palabra o por número.
 *
 * Sin consulta devuelve la tabla entera: la lista completa es el estado por
 * defecto de la pieza, no un estado vacío que haya que llenar escribiendo.
 * Devuelve `[{ numero, sueno }]`.
 */
export function buscarSuenos(consulta) {
  const todo = TABLA_SUENOS.map((sueno, numero) => ({ numero, sueno }));
  const q = normalizar(consulta);
  if (!q) return todo;

  // Si escribió dígitos, se busca por número: "5" trae el 05, "1" trae el 01,
  // el 10..19 y el 21, 31… Se ordena para que el match exacto salga primero.
  if (/^\d{1,2}$/.test(q)) {
    const exacto = Number(q);
    return todo
      .filter((f) => f.numero === exacto || String(f.numero).padStart(2, '0').includes(q))
      .sort((a, b) => (a.numero === exacto ? -1 : b.numero === exacto ? 1 : a.numero - b.numero));
  }

  return todo.filter((f) => normalizar(f.sueno).includes(q));
}

/** El sueño de un número de 2 cifras, o `null` si no está en rango. */
export function suenoDe(dosCifras) {
  const n = Number(dosCifras);
  return Number.isInteger(n) && n >= 0 && n <= 99 ? TABLA_SUENOS[n] : null;
}

/**
 * Los juegos del simulador.
 *
 * `familia` decide el molde:
 *   'quiniela'   → número de 1 a 4 cifras + posición de salida
 *   'bolillero'  → grilla de números; opcionalmente una bola extra aparte
 *
 * `bola` es el relleno de la bolilla en el talón oscuro (#0B1F1E). Todas
 * llevan la tinta `#08191E` encima y todas pasan **AAA (7:1)**, medido:
 *   lotoplus #FF8877 → 7.74 · quini6 #3FCDEC → 9.55 · brinco #FFE23D → 13.87
 *   loto5plus #2ED27A → 9.09 · tombola #FFA733 → 9.27 · Plus #F4B740 → 10.01
 * El color de marca crudo (`acento` de `temasJuegos.js`) NO lleva texto
 * nunca: vive solo en el filete de la cabecera. Es la misma separación
 * acento/tinta que ya resolvió el carrusel del hero.
 */
export const JUEGOS_SIM = [
  {
    clave: 'tombola',
    nombre: 'Tómbola Jujeña',
    familia: 'quiniela',
    bola: '#FFA733',
    cifrasMax: 4,
    posiciones: 20,
    comoSeJuega: 'Elegís un número de 1 a 4 cifras, entre el 0000 y el 9999, y la posición de salida en la que querés que salga.',
    detalle: 'Acertás si tu número sale justo en esa posición del extracto.',
    fuente: 'Mecánica según Lotería de la Ciudad. Las 20 posiciones se corroboran con el extracto real de Jujuy que publica este sitio.',
  },
  {
    clave: 'lotoplus',
    nombre: 'Loto Plus',
    familia: 'bolillero',
    bola: '#FF8877',
    cantidad: 6,
    min: 0,
    max: 45,
    extra: {
      nombre: 'Número Plus',
      corto: 'Plus',
      min: 0,
      max: 9,
      bola: '#F4B740',
      nota: 'Va aparte del bolillero y triplica el premio.',
    },
    comoSeJuega: 'Elegís 6 números del 0 al 45, y un Número Plus del 0 al 9.',
    detalle: 'Con un solo ticket participás en las cuatro modalidades.',
    modalidades: ['Tradicional', 'Match', 'Desquite', 'Sale o Sale'],
    fuente: 'Mecánica según el sitio oficial de Loto Plus (Lotería de la Ciudad).',
  },
  {
    clave: 'quini6',
    nombre: 'Quini 6',
    familia: 'bolillero',
    bola: '#3FCDEC',
    cantidad: 6,
    min: 0,
    max: 45,
    comoSeJuega: 'Elegís 6 números sobre un total de 46, del 00 al 45.',
    detalle: 'Para jugar a Revancha y a Siempre Sale hay que apostar primero a Tradicional.',
    modalidades: ['Tradicional', 'La Segunda', 'Revancha', 'Siempre Sale'],
    fuente: 'Mecánica según las preguntas frecuentes de Lotería de Santa Fe.',
  },
  {
    clave: 'brinco',
    nombre: 'Brinco',
    familia: 'bolillero',
    bola: '#FFE23D',
    cantidad: 6,
    min: 0,
    max: 39,
    comoSeJuega: 'Elegís 6 números del bolillero de 40 bolillas, del 0 al 39.',
    detalle: 'Se premia con 6, 5, 4 y 3 aciertos.',
    modalidades: ['Brinco', 'Brinco Junior Siempre Sale'],
    fuente: 'Mecánica según las preguntas frecuentes de Lotería de Santa Fe.',
  },
  {
    clave: 'loto5plus',
    nombre: 'Loto 5 Plus',
    familia: 'bolillero',
    bola: '#2ED27A',
    cantidad: 5,
    min: 0,
    max: 36,
    comoSeJuega: 'Elegís 5 números entre el 0 y el 36.',
    detalle: 'El sorteo se hace con una ruleta francesa: el croupier tira las suertes hasta obtener 5 números distintos.',
    fuente: 'Mecánica según el sitio oficial de Loto 5 Plus (Lotería de la Ciudad).',
  },
];

/** El juego por su clave, o el primero (la Tómbola, el juego de la casa). */
export function juegoDe(clave) {
  return JUEGOS_SIM.find((j) => j.clave === clave) || JUEGOS_SIM[0];
}

/** `n` números distintos del rango [min, max], en orden ascendente. */
export function jugadaAlAzar({ cantidad, min, max }) {
  const bolsa = [];
  for (let v = min; v <= max; v += 1) bolsa.push(v);
  for (let i = bolsa.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [bolsa[i], bolsa[j]] = [bolsa[j], bolsa[i]];
  }
  return bolsa.slice(0, cantidad).sort((a, b) => a - b);
}

/** Un entero al azar en [min, max]. */
export function alAzarEntre(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}
